import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {parseUnifiedDiff} from '../utils/commitDiffParser';
import './UnifiedDiffView.css';

// A rendered `git show` / `git diff`: one collapsible section per file (path, status, +N −M), hunk headers, old/new
// line numbers, added / removed lines in the theme's success / error colours — and a visible +/− prefix plus a
// screen-reader word, so the change is never told by colour alone. The text is parsed by utils/commitDiffParser.js.
// Large diffs stay light: past `maxRows` rows a file shows a "Show all" button, and with many files the ones past the
// first few start collapsed.

const STATUS_LABELS = {
  added: 'Added',
  deleted: 'Deleted',
  renamed: 'Renamed',
  copied: 'Copied',
};

const MANY_FILES = 12;
const OPEN_WHEN_MANY = 5;

function rowsOf(file) {
  const out = [];
  file.hunks.forEach((hunk, hunkIndex) => {
    out.push({kind: 'hunk', hunk, key: `h${hunkIndex}`});
    hunk.rows.forEach((row, rowIndex) => out.push({kind: 'row', row, key: `h${hunkIndex}r${rowIndex}`}));
  });
  return out;
}

function DiffRow({row}) {
  const marker = row.type === 'add' ? '+' : row.type === 'del' ? '−' : ' ';
  return (
    <tr className={`udv-row udv-row-${row.type}`}>
      <td className="udv-gutter">{row.type !== 'add' ? row.oldLineNum : ''}</td>
      <td className="udv-gutter">{row.type !== 'del' ? row.newLineNum : ''}</td>
      <td className="udv-marker">
        <span aria-hidden="true">{marker}</span>
        {row.type !== 'ctx' && <span className="udv-sr">{row.type === 'add' ? 'added' : 'removed'}</span>}
      </td>
      <td className="udv-text">
        <span>{row.text}</span>
        {row.noNewline && (
          <span aria-label="no newline at end of file" className="udv-nonewline" role="img" title="No newline at end of file">⊘</span>
        )}
      </td>
    </tr>
  );
}

DiffRow.propTypes = {row: PropTypes.object.isRequired};

function DiffFile({file, defaultOpen, maxRows}) {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  const rows = useMemo(() => rowsOf(file), [file]);
  const capped = !showAll && rows.length > maxRows;
  const visible = capped ? rows.slice(0, maxRows) : rows;
  const lineCount = file.hunks.reduce((n, h) => n + h.rows.length, 0);
  const renamed = (file.status === 'renamed' || file.status === 'copied') && file.oldPath && file.oldPath !== file.newPath;
  const bodyId = `udv-file-${file.path}`.replace(/[^A-Za-z0-9_-]/g, '_');

  return (
    <section className={`udv-file${open ? ' udv-file-open' : ''}`} data-testid="udv-file">
      <button
        aria-controls={bodyId}
        aria-expanded={open}
        className="udv-file-header"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span aria-hidden="true" className="udv-chevron">{open ? '▾' : '▸'}</span>
        {renamed ? (
          <span className="udv-file-path">
            <span className="udv-file-oldpath">{file.oldPath}</span>
            <span aria-label="renamed to"> → </span>
            <span>{file.newPath}</span>
          </span>
        ) : (
          <span className="udv-file-path">{file.path}</span>
        )}
        {STATUS_LABELS[file.status] && (
          <span className={`udv-badge udv-badge-${file.status}`}>{STATUS_LABELS[file.status]}</span>
        )}
        {file.binary && <span className="udv-badge">Binary</span>}
        <span className="udv-file-stats">
          {file.additions > 0 && <span className="udv-stat-add" title={`${file.additions} added`}>+{file.additions}</span>}
          {file.deletions > 0 && <span className="udv-stat-del" title={`${file.deletions} removed`}>−{file.deletions}</span>}
        </span>
      </button>
      {open && (
        <div className="udv-file-body" id={bodyId}>
          {file.binary && <div className="udv-note">Binary file — not shown.</div>}
          {!file.binary && lineCount === 0 && (
            <div className="udv-note">
              {renamed && file.similarity === 100 ? 'Renamed without changes.' : 'No content changes.'}
            </div>
          )}
          {lineCount > 0 && (
            <table className="udv-hunk">
              <tbody>
                {visible.map((item) => (item.kind === 'hunk' ? (
                  <tr className="udv-row udv-row-hunk" key={item.key}>
                    <td className="udv-hunk-header" colSpan={4}>
                      <span className="udv-hunk-range">{(item.hunk.header.match(/^@@[^@]*@@/) || [item.hunk.header])[0]}</span>
                      {item.hunk.section && <span className="udv-hunk-section">{item.hunk.section}</span>}
                    </td>
                  </tr>
                ) : <DiffRow key={item.key} row={item.row} />))}
              </tbody>
            </table>
          )}
          {capped && (
            <button className="udv-show-all" onClick={() => setShowAll(true)} type="button">
              Show all {lineCount} lines ({rows.length - visible.length} more)
            </button>
          )}
        </div>
      )}
    </section>
  );
}

DiffFile.propTypes = {
  defaultOpen: PropTypes.bool.isRequired,
  file: PropTypes.object.isRequired,
  maxRows: PropTypes.number.isRequired,
};

export default function UnifiedDiffView({diffText, files: parsedFiles, maxRows = 400}) {
  const files = useMemo(() => parsedFiles || parseUnifiedDiff(diffText), [parsedFiles, diffText]);

  if (files.length === 0) {
    return <div className="udv-empty">No changes to show.</div>;
  }

  const many = files.length > MANY_FILES;
  return (
    <div className="udv-root">
      {files.length > 1 && (
        <div className="udv-summary">
          {files.length} files changed
          <span className="udv-stat-add">+{files.reduce((n, f) => n + f.additions, 0)}</span>
          <span className="udv-stat-del">−{files.reduce((n, f) => n + f.deletions, 0)}</span>
        </div>
      )}
      {files.map((file, index) => (
        <DiffFile
          defaultOpen={!many || index < OPEN_WHEN_MANY}
          file={file}
          // eslint-disable-next-line react/no-array-index-key
          key={`${index}:${file.path}`}
          maxRows={maxRows}
        />
      ))}
    </div>
  );
}

UnifiedDiffView.propTypes = {
  diffText: PropTypes.string,
  files: PropTypes.array,
  maxRows: PropTypes.number,
};
